/**
 * TanStack Query hooks for /api/notifications/*.
 *
 *   - `useNotifications()` — list + unread count.
 *   - `useNotificationSocket()` — websocket invalidation channel so the
 *     badge and account page update without a full reload.
 *   - `useRespondNotification()` — POST /respond {action: accept|decline}.
 *     Used for actionable invites/transfers.
 *   - `useMarkRead()` — POST /read for informational notifications.
 *   - `useDeleteAllNotifications()` — DELETE all notifications for the user.
 *
 * Mutations invalidate `["notifications"]`; respond also invalidates
 * `["cars"]` since accepting a share/transfer changes the cars list.
 */

import { useEffect } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { fetchJson, postJson } from "./client";
import type { NotificationsResponse } from "./types";

export const notifsKey = ["notifications"] as const;

export function useNotifications(options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: notifsKey,
    queryFn: () => fetchJson<NotificationsResponse>("/api/notifications"),
    enabled: options?.enabled ?? true,
    // WebSocket events drive live updates; focus refetch is just a fallback.
    refetchOnWindowFocus: true,
    staleTime: 30_000,
  });
}

export function useNotificationSocket(enabled: boolean) {
  const qc = useQueryClient();

  useEffect(() => {
    if (!enabled || typeof window === "undefined") return;

    let socket: WebSocket | null = null;
    let retryTimer: number | undefined;
    let closed = false;
    let attempts = 0;

    function connect() {
      const protocol = window.location.protocol === "https:" ? "wss:" : "ws:";
      socket = new WebSocket(
        `${protocol}//${window.location.host}/api/notifications/ws`,
      );

      socket.onopen = () => {
        attempts = 0;
      };

      socket.onmessage = (event) => {
        let payload: unknown;
        try {
          payload = JSON.parse(event.data);
        } catch {
          return;
        }
        if (
          payload &&
          typeof payload === "object" &&
          (payload as { type?: string }).type === "notifications_changed"
        ) {
          void qc.invalidateQueries({ queryKey: notifsKey });
        }
      };

      socket.onerror = () => {
        socket?.close();
      };

      socket.onclose = () => {
        if (closed) return;
        const delayMs = Math.min(30_000, 1000 * 2 ** attempts);
        attempts += 1;
        retryTimer = window.setTimeout(connect, delayMs);
      };
    }

    connect();

    return () => {
      closed = true;
      if (retryTimer !== undefined) window.clearTimeout(retryTimer);
      socket?.close();
    };
  }, [enabled, qc]);
}

export function useRespondNotification() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      action,
    }: {
      id: number;
      action: "accept" | "decline";
    }) =>
      postJson<{ ok: true; status: string }>(
        `/api/notifications/${id}/respond`,
        { action },
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: notifsKey });
      qc.invalidateQueries({ queryKey: ["cars"] });
      qc.invalidateQueries({ queryKey: ["auth", "me"] });
    },
  });
}

export function useMarkNotificationRead() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) =>
      postJson<{ ok: true }>(`/api/notifications/${id}/read`, {}),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: notifsKey });
    },
  });
}

export function useDeleteAllNotifications() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () =>
      fetchJson<{ ok: true; deleted: number }>("/api/notifications", {
        method: "DELETE",
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: notifsKey });
    },
  });
}
